import React, { Component } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { fetchData } from 'reducers/dataTable';
// components
import { FormattedMessage } from 'react-intl';
import Button from 'components/Button';
import DateRangePicker from 'components/DatePicker/DateRangePicker';
import ReactSelect from 'components/Select/ReactSelect';
import TemperaturePanel from '../components/TemperaturePanel';
import BarnsheetsTemperatureTable from './BarnsheetsTemperatureTable';
// api
import { getPigGroupTemperaturesCSV } from 'endpoints/pigGroups';
// utils
import cn from 'classnames';
import moment from 'moment';
import download from 'downloadjs';
import { calcSetPointValue } from 'utils';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { getDatesFromRange } from 'utils/treatmentChartsHelper.js';
// constants
import { antibioticStatsOptions } from 'constants.js';
// styles
import './BarnsheetsTemperature.scss';

const getTemperatureUnit = (temperatureSystem) => (temperatureSystem === 'celsius' ? '°C' : '°F');

const periodOptions = [
  { name: <FormattedMessage id="component.statsOptions.lifeOfGroup" />, value: 'life_of_group' },
  ...antibioticStatsOptions,
];

const prepareResources = (resources, temperatureSystem) => {
  const unit = getTemperatureUnit(temperatureSystem);
  const formatLabel = (value) => (value || value === 0 ? `${value}${unit}` : 'N/A');

  return resources.map((resource) => {
    const { created_on, high_temperature, low_temperature, set_point } = resource;
    const high = calcSetPointValue(high_temperature, temperatureSystem);
    const low = calcSetPointValue(low_temperature, temperatureSystem);
    const set_point_value = calcSetPointValue(set_point, temperatureSystem);

    return {
      ...resource,
      high,
      low,
      set_point_value,
      high_label: formatLabel(high),
      low_label: formatLabel(low),
      set_point_label: formatLabel(set_point_value),
      date: moment(created_on, 'YYYY-MM-DD').format('MMM DD'),
      tooltip_date: moment(created_on).format('MMM DD, YYYY'),
    };
  });
};

class BarnsheetsTemperature extends Component {
  constructor(props) {
    super(props);

    this.state = {
      period_name: 'life_of_group',
      startDate: null,
      endDate: null,
    };
  }

  componentDidMount = () => {
    const { fetchData } = this.props;

    fetchData();
  };

  getPeriodDates = () => {
    const { period_name, startDate, endDate } = this.state;

    if (period_name === 'life_of_group') {
      const { pigGroup } = this.props;
      return {
        startDate: pigGroup.started_on ? moment(pigGroup.started_on, 'YYYY-MM-DD') : null,
        endDate: moment(),
      };
    }

    return { startDate, endDate };
  }

  downloadCSV = (e) => {
    e.stopPropagation();
    const { pigGroup: { id, name } } = this.props;
    const { startDate, endDate } = this.getPeriodDates();

    const date_start = startDate ? startDate.format('YYYY-MM-DD') : '';
    const date_end = (endDate || moment()).format('YYYY-MM-DD');

    getPigGroupTemperaturesCSV(id, { date_start, date_end })
      .then((blob) => download(blob, `EveryPig_Group_${name}_Temperature_${date_start || 'start'}_${date_end}.csv`,
        'text/csv'))
      .catch(toastResponseErrors);
  }

  sortByNewest = (data) => [...data.sort((a, b) => moment(b.created_on).diff(moment(a.created_on)))]

  sortByOldest = (data) => [...data.sort((a, b) => moment(a.created_on).diff(moment(b.created_on)))]

  filterResources = (resources) => {
    const { startDate, endDate } = this.getPeriodDates();
    let filtered = resources;

    if (startDate && endDate) {
      filtered = resources.filter(({ created_on }) => moment(
        created_on,
        'YYYY-MM-DD'
      ).isBetween(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'), null, '[]'));
    }

    return filtered;
  }

  onDateChange = ({ startDate, endDate }) => {
    this.setState({ startDate, endDate });
  }

  onPeriodSelect = (option) => {
    if (!option) return;
    const { value } = option;
    if (value === this.state.period_name) return;
    if (value === 'life_of_group') {
      this.setState({ period_name: value, startDate: null, endDate: null });
      return;
    }
    this.setState({ period_name: value, ...getDatesFromRange(value) });
  };

  render() {
    const { isLoading, resources, temperatureSystem } = this.props;
    const filteredResources = this.filterResources(resources);
    // Chart plots only days with reported temps, the table below keeps all days
    const chartResources = filteredResources.filter(({ high, low }) => (
      high || high === 0 || low || low === 0
    ));
    const { startDate, endDate, period_name } = this.state;

    return (
      <div className={cn('BarnsheetsTemperature')}>
        <div className={cn('mb-20 filters-wrap')}>
          {period_name === 'custom' && (
            <DateRangePicker
              className={cn('date-picker')}
              startDate={startDate}
              endDate={endDate}
              isOutsideRange={(day) => (day.isAfter(moment(), 'day'))}
              onDatesChange={this.onDateChange}
            />
          )}
          <ReactSelect
            className={cn('select-period')}
            placeholder={<FormattedMessage id="general.selectPeriod" />}
            options={periodOptions}
            valueKey="value"
            labelKey="name"
            value={period_name}
            onChange={this.onPeriodSelect}
            noInputMargin
            searchable={false}
          />
          <Button className={cn('download-button')} primary onClick={this.downloadCSV} disabled={isLoading}>
            <FormattedMessage id="general.button.download" />
          </Button>
        </div>
        <TemperaturePanel
          isLoading={isLoading}
          temperatureData={this.sortByOldest(chartResources)}
          temperatureUnit={getTemperatureUnit(temperatureSystem)}
        />
        <BarnsheetsTemperatureTable
          data={this.sortByNewest(filteredResources)}
        />
      </div>
    );
  }
}

BarnsheetsTemperature.contextTypes = {
  router: T.object.isRequired,
};

BarnsheetsTemperature.propTypes = {
  fetchData: T.func.isRequired,
  pigGroup: T.object.isRequired,
  isLoading: T.bool.isRequired,
  resources: T.array.isRequired,
  temperatureSystem: T.string.isRequired,
};

export default connect(
  (state) => ({
    resources: prepareResources(
      state.dataTable.barnsheetsTemperature.resources,
      state.auth.user.current_company.temperature_system
    ),
    isLoading: state.dataTable.barnsheetsTemperature.isLoading,
    pigGroup: state.barnsheets.groupCheckups.pigGroup,
    temperatureSystem: state.auth.user.current_company.temperature_system,
  }), (dispatch, { params }) => (
    {
      fetchData: () => dispatch(fetchData(`/pig_groups/${params.id}/daily_checkup_temperatures`,
        'barnsheetsTemperature')),
    }
  )
)(BarnsheetsTemperature);
